import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '../../contexts/AuthContext';
import {
  Search, FolderKanban, ChevronLeft, ChevronRight, Clock, Building2,
  Eye, RefreshCw, Globe, CheckCircle2, Loader2, MapPin
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 6;

const STATUS_CONFIG = {
  in_progress: { color: 'bg-blue-100 text-blue-700',   label: 'In Progress', bar: 'bg-blue-500' },
  final_stage: { color: 'bg-orange-100 text-orange-700', label: 'Final Stage', bar: 'bg-orange-500' },
  completed:   { color: 'bg-emerald-100 text-emerald-700', label: 'Completed', bar: 'bg-emerald-500' },
};

const ContractorProjects = () => {
  const { token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [newProgress, setNewProgress] = useState(50);
  const [updatingProgress, setUpdatingProgress] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contractor/projects`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE },
      });
      setProjects(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      setProjects([]);
      setTotal(0);
    } finally { setLoading(false); }
  }, [token, page]);

  useEffect(() => { load(); }, [load]);

  const filtered = projects.filter(p => {
    const q = search.toLowerCase();
    return (!q || (p.client || '').toLowerCase().includes(q) || (p.brief || '').toLowerCase().includes(q) || (p.city || '').toLowerCase().includes(q)) &&
      (statusFilter === 'all' || p.status === statusFilter);
  });

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const openDetail = (p) => {
    setSelected(p);
    setNewProgress(p.progress ?? 50);
  };

  const handleProgressUpdate = async () => {
    if (!selected) return;
    setUpdatingProgress(true);
    try {
      await axios.patch(
        `${API}/contractor/proposals/${selected.id}/progress`,
        { progress: newProgress },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProjects(prev => prev.map(p => p.id === selected.id ? { ...p, progress: newProgress } : p));
      setSelected(prev => ({ ...prev, progress: newProgress }));
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to update progress');
    } finally { setUpdatingProgress(false); }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Projects</h1>
          <p className="text-sm text-gray-500 mt-0.5">Active and completed projects from accepted proposals</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">LIVE</span>
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5 h-8">
            <RefreshCw className="h-3.5 w-3.5" />Refresh
          </Button>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Active',    value: projects.filter(p => p.status !== 'completed').length, color: 'text-blue-600' },
          { label: 'Completed', value: projects.filter(p => p.status === 'completed').length, color: 'text-emerald-600' },
          { label: 'Total Value', value: total > 0 ? `${Math.round(projects.reduce((a, p) => a + (p.value || 0), 0) / 1000)}K+` : '—', color: 'text-green-600' },
        ].map((s, i) => (
          <Card key={i} className="border-0 shadow-sm">
            <div className="p-4 flex items-center gap-3">
              <span className={`text-2xl font-bold ${s.color}`}>{s.value}</span>
              <span className="text-sm text-gray-500">{s.label}</span>
            </div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <div className="p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input placeholder="Search client, brief or city..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="pl-9 bg-gray-50 border-gray-200" />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500">
            <option value="all">All Statuses</option>
            <option value="in_progress">In Progress</option>
            <option value="final_stage">Final Stage</option>
            <option value="completed">Completed</option>
          </select>
          <span className="text-sm text-gray-400 ml-auto">{total} projects</span>
        </div>
      </Card>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="animate-spin h-7 w-7 text-green-400" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FolderKanban className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="font-semibold text-gray-600">No projects yet</p>
          <p className="text-sm mt-2 text-gray-400 max-w-xs mx-auto">
            When an exhibitor accepts your proposal, it will appear here automatically.
          </p>
          <button
            data-testid="go-brief-board-btn"
            onClick={() => window.location.href = '#/app/contractor/brief-board'}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors">
            <Globe className="h-4 w-4" />Browse Open Briefs
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(p => {
            const sc = STATUS_CONFIG[p.status] || STATUS_CONFIG.in_progress;
            return (
              <Card key={p.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="text-sm font-bold text-gray-900">{p.brief}</p>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${sc.color}`}>{sc.label}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                        <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{p.client}</span>
                        {p.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{p.city}</span>}
                        {p.deadline && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(p.deadline).toLocaleDateString('en-GB')}</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-100 rounded-full h-2">
                          <div className={`h-2 rounded-full ${sc.bar}`} style={{ width: `${p.progress || 0}%` }} />
                        </div>
                        <span className="text-xs text-gray-500 w-8 text-right">{p.progress || 0}%</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <span className="text-base font-bold text-gray-900">{p.value?.toLocaleString()} {p.currency}</span>
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                        data-testid={`project-detail-btn-${p.id}`}
                        onClick={() => openDetail(p)}>
                        <Eye className="h-3 w-3" />Details
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} / {total}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="h-7 px-2" disabled={page === 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            {[...Array(totalPages)].map((_, i) => (
              <Button key={i} variant={page === i + 1 ? 'default' : 'outline'} size="sm" className="h-7 w-7 p-0 text-xs" onClick={() => setPage(i + 1)}>{i + 1}</Button>
            ))}
            <Button variant="outline" size="sm" className="h-7 px-2" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold text-gray-900">Project Details</h3>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setSelected(null)}>✕</Button>
            </div>

            <div className="space-y-2 text-sm mb-5">
              {[
                ['Client', selected.client],
                ['Show / Brief', selected.brief],
                ['City', selected.city || '—'],
                ['Status', STATUS_CONFIG[selected.status]?.label],
                ['Value', `${selected.value?.toLocaleString()} ${selected.currency}`],
                ['Started', selected.started || '—'],
                ['Deadline', selected.deadline ? new Date(selected.deadline).toLocaleDateString('en-GB') : '—'],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between py-2 border-b border-gray-50">
                  <span className="text-gray-500">{k}</span>
                  <span className="font-medium text-right">{v}</span>
                </div>
              ))}
            </div>

            {/* Progress Update */}
            {selected.status !== 'completed' && (
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Update Progress</p>
                <div className="flex items-center gap-3">
                  <input
                    type="range" min="0" max="100" step="5"
                    value={newProgress}
                    onChange={e => setNewProgress(Number(e.target.value))}
                    className="flex-1 accent-green-600"
                    data-testid="progress-slider"
                  />
                  <span className="text-sm font-bold text-gray-800 w-10 text-right">{newProgress}%</span>
                </div>
                <Button
                  data-testid="btn-update-progress"
                  onClick={handleProgressUpdate}
                  disabled={updatingProgress || newProgress === selected.progress}
                  size="sm"
                  className="w-full bg-green-600 hover:bg-green-700 text-white gap-1.5"
                >
                  {updatingProgress
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <CheckCircle2 className="h-3.5 w-3.5" />
                  }
                  {newProgress === 100 ? 'Mark as Complete' : 'Save Progress'}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractorProjects;
